<template>
  <PageWrap page-title="Agenda de visitas">
    <b-row v-if="isDesktop">
      <b-col cols="12">
        <BfTitle size="small" tag="h3" class="mb-3">Visitas agendadas</BfTitle>
        <LoadingIndicator v-if="loading"/>

        <template v-else>
          <VisitTable
            :data="scheduledVisitsData"
            allow-select
            max-height="500"
            :selected-rows="selectedTableRows"
            @row-checkbox-change="rowCheckboxChange"
          />

          <div v-if="tableHasSelectedRows" class="mt-4">
            <div class="visit-radio-options-box">
              <BfTitle size="small" tag="h5" class="mb-4">
                Completar las visitas seleccionadas y:
              </BfTitle>

              <BfCheckbox
                v-for="{ label, id, active } in radioFieldOptions"
                :key="id"
                data-key="visit_radio"
                :value="active"
                :label="label"
                :bottom-gutter="true"
                class="visit-radio-option"
                @update="updateRadioField(id)"
              />
            </div>
            <div class="mt-3">
              <ScheduleVisitForm
              v-if="showHora"
              class="mt-4 align-items-end"
              @update="updateScheduleVisitForm"
              >
              </ScheduleVisitForm>
            </div>

            <div class="mt-3">
              <BfButton @click="completeVisitsClick" class="mr-4">{{completeButtonLabel}}</BfButton>
              <BfButton
                @click="setTimeCompleteVisitsClick"
                variant="orange"
                class="mr-4"
              >{{setTimeCompleteAButtonLabel}}</BfButton>
              <BfButton @click="deleteVisitsClick" variant="red">{{deleteButtonLabel}}</BfButton>
            </div>
          </div>
        </template>
      </b-col>
    </b-row>

    <MobileView
      v-else
      :cards="scheduledVisitsData"
      :loading="loading"
      @card-click-complete="mobileCompleteVisitClick"
    />
  </PageWrap>
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash.get';
import PageWrap from '@/components/PageWrap';
import LoadingIndicator from '@/components/LoadingIndicator';
import VisitTable from '@/components/Tables/VisitTable';
import ScheduleVisitForm from '@/components/FormGroups/ScheduleVisitForm';
import { isDesktop } from '@/utils';
import { chargeAPI, visitAPI } from '@/api';

import MobileView from './MobileView';

export default {
  name: 'VisitsSchedule',

  data: () => ({
    loading: false,
    selectedTableRows: [],
    scheduledVisitsData: [],
    selectedRadioOption: 1,
    scheduleDateValue: '',
    scheduleDurationValue: 1,
    duration: '',
    scheduleForm: {},
    hora: '',
    timestamp: '',
    showHora: false,
    userKey: '',
  }),

  async created() {
    this.loading = true;
    try {
      await this.loadScheduledVisits();
    } finally {
      this.loading = false;
    }
  },

  methods: {
    updateScheduleVisitForm({ key, value }) {
      this.usedKey = key;
      this.timestamp = value;
    },

    scheduleVisitFormObject() {
      return {
        ...this.scheduleForm,
        duration: this.scheduleDurationValue,
      };
    },
    async loadScheduledVisits() {
      this.selectedTableRows = [];
      const { data } = await visitAPI.getAllByAssessor(this.tasadorId);
      this.scheduledVisitsData = data.data;
    },

    rowCheckboxChange({ value, row }) {
      const { id } = row;

      if (value) {
        this.selectedTableRows.push(id);
      } else {
        const itemIndex = this.selectedTableRows.indexOf(id);
        this.$delete(this.selectedTableRows, itemIndex);
      }
    },

    async completeVisitsClick() {
      if (this.tableHasSelectedRows) {
        /* eslint-disable-next-line arrow-parens */
        const requests = this.selectedTableRows.map(async id => {
          const { message, data } = await visitAPI.setComplete(id);

          return {
            message,
            chargeId: data.encargos_id,
          };
        });

        try {
          const updatedVisits = await Promise.all(requests);
          const radioOpt = this.selectedRadioOption;

          const secondRequests = updatedVisits.map(async ({ message, chargeId }) => {
            if (radioOpt !== 3) {
              let targetStatus;

              if (radioOpt === 1) {
                targetStatus = 'SE';
              } else if (radioOpt === 2) {
                targetStatus = 'SB';
              } else if (radioOpt === 3) {
                targetStatus = 'SC';
              }

              await chargeAPI.changeStatus({
                id: chargeId,
                status: targetStatus,
              });
            }

            return message;
          });

          const messages = await Promise.all(secondRequests);

          /* eslint-disable-next-line array-callback-return */
          messages.map((message, index) => {
            /* The following `setTimeout` is necessary to prevent the
            notifications sometimes overlapping */
            setTimeout(
              () =>
                this.$bfNotify({
                  title: 'Actualizado',
                  message,
                  type: 'success',
                }),
              10 * index
            );
          });

          this.loadScheduledVisits();
        } catch (error) {
          this.$bfNotify({
            title: 'Error',
            message: (error && error.error) || 'Parece que faltan fotos',
            type: 'error',
          });
        }
      }
    },

    async setTimeCompleteVisitsClick() {
      this.showHora = true;
      const radioOpt = this.selectedRadioOption;
      let targetStatus = '';
      if (radioOpt === 1) {
        targetStatus = 'SE';
      } else if (radioOpt === 2) {
        targetStatus = 'SB';
      } else if (radioOpt === 3) {
        targetStatus = 'SC';
      }


      if (this.timestamp) {
        await visitAPI.setCompleteTimestamp({
          hora: this.timestamp,
          selectedTableRows: this.selectedTableRows,
          newStatus: targetStatus,
        });
      }

      this.loadScheduledVisits();
    },

    async deleteVisitsClick() {
      if (this.tableHasSelectedRows) {
        /* eslint-disable-next-line arrow-parens */
        const requests = this.selectedTableRows.map(async id => {
          const { message } = await visitAPI.unschedule(id);
          this.$bfNotify({ title: 'Eliminado', message, type: 'success' });
        });

        await Promise.all(requests);

        this.loadScheduledVisits();
      }
    },

    updateRadioField(id) {
      this.selectedRadioOption = id;
    },

    async mobileCompleteVisitClick(visitId) {
      try {
        const { message, data } = await visitAPI.setComplete(visitId);

        await chargeAPI.changeStatus({
          id: data.encargos_id,
          status: 'SE',
        });

        this.$bfNotify({
          title: 'Actualizado',
          message,
          type: 'success',
        });

        this.loadScheduledVisits();
      } catch (error) {
        this.$bfNotify({
          title: 'Error',
          message: (error && error.error) || '',
          type: 'error',
        });
      }
    },
  },

  computed: {
    ...mapState({ tasadorId: ({ auth }) => get(auth, ['user', 'tasador_id']) }),

    tableHasSelectedRows() {
      return !!this.selectedTableRows.length;
    },

    completeButtonLabel() {
      return `Completar visita${this.pluralString}`;
    },
    setTimeCompleteAButtonLabel() {
      return `Establecer hora y completar visita${this.pluralString}`;
    },
    deleteButtonLabel() {
      return `Eliminar visita${this.pluralString}`;
    },

    pluralString() {
      return this.selectedTableRows.length > 1 ? 's' : '';
    },

    radioFieldOptions() {
      return [
        {
          label: 'Pasar los encargos a asignación de salidas',
          id: 1,
        },
        {
          label: 'Volver de nuevo los encargos a asignación de visitas',
          id: 2,
        },
        {
          label: 'Mantener los encargos para mi en visitas',
          id: 3,
        },
      ].map(opt => ({
        ...opt,
        active: this.selectedRadioOption === opt.id,
      }));
    },

    isDesktop() {
      return isDesktop(this.$vssWidth);
    },
  },

  components: {
    LoadingIndicator,
    MobileView,
    PageWrap,
    ScheduleVisitForm,
    VisitTable,
  },
};
</script>

<style lang="scss" scoped>
.visit-radio-options-box {
  display: inline-block;
  border: 1px solid #c8c8c8;
  padding: 20px 30px 5px 20px;
}

.visit-radio-option {
  /deep/ {
    .bf-label-wrap {
      display: none;
    }

    .el-checkbox__inner {
      border-radius: 50%;
    }
  }
}
</style>
